'use client';

import React, { useEffect, useRef, useCallback, useState, useMemo } from 'react';

interface YouTubePlayerV3Props {
  videoId: string;
  className?: string;
  isPlaying?: boolean;
  currentTime?: number;
  onReady?: (player: any) => void;
  onTimeUpdate?: (currentTime: number) => void;
  onDurationChange?: (duration: number) => void;
  onPlayPause?: (isPlaying: boolean) => void;
  onEnded?: () => void;
  title?: string;
  muted?: boolean;
}

// 声明 YouTube API
declare global {
  interface Window {
    YT: any;
    onYouTubeIframeAPIReady: () => void;
  }
}

// 播放器控制接口 - 与内部状态完全隔离
interface PlayerControlInterface {
  seekTo: (time: number, allowSeekAhead?: boolean) => Promise<void>;
  playVideo: () => Promise<void>;
  pauseVideo: () => Promise<void>;
  getCurrentTime: () => number;
  getDuration: () => number;
  setPlaybackRate: (rate: number) => void;
  getPlayerState: () => number;
}

// 播放器内部状态
interface InternalPlayerState {
  isReady: boolean;
  isPlaying: boolean;
  currentTime: number;
  duration: number;
  playerState: number;
}

export function YouTubePlayerV3({
  videoId,
  className,
  isPlaying,
  currentTime,
  onReady,
  onTimeUpdate,
  onDurationChange,
  onPlayPause,
  onEnded,
  muted = false
}: YouTubePlayerV3Props) {
  const playerRef = useRef<any>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  
  // 播放器内部状态 - 完全独立
  const [internalState, setInternalState] = useState<InternalPlayerState>({
    isReady: false,
    isPlaying: false,
    currentTime: 0,
    duration: 0,
    playerState: -1
  });
  
  
  // API就绪状态
  const [isAPIReady, setIsAPIReady] = useState(false);
  const [playerError, setPlayerError] = useState<{ code: number | string; message: string } | null>(null);
  
  // 外部控制命令队列 - 使用异步命令模式而不是直接状态同步
  const commandQueueRef = useRef<Array<() => Promise<void>>>([]);
  const processingCommandRef = useRef(false);
  
  // 使用 ref 来避免闭包陷阱
  const callbackRefs = useRef({
    onReady,
    onDurationChange,
    onTimeUpdate,
    onPlayPause,
    onEnded
  });
  
  // 更新回调引用
  useEffect(() => {
    callbackRefs.current = {
      onReady,
      onDurationChange,
      onTimeUpdate,
      onPlayPause,
      onEnded
    };
  });
  
  // 创建稳定的播放器控制接口
  const playerControlInterface = useMemo<PlayerControlInterface>(() => ({
    seekTo: async (time: number, allowSeekAhead: boolean = true) => {
      return new Promise((resolve) => {
        // 🔥 关键修复：如果播放器就绪，直接执行seek，不使用命令队列
        if (playerRef.current && typeof playerRef.current.seekTo === 'function' && internalState.isReady) {
          // 直接执行seek操作
          isUserSeekingRef.current = true;
          
          playerRef.current.seekTo(time, allowSeekAhead);
          
          // 立即更新内部状态，确保同步
          setInternalState(prev => ({
            ...prev,
            currentTime: time
          }));
          
          // 清除seeking标志
          setTimeout(() => {
            isUserSeekingRef.current = false;
          }, 1500);
          
          resolve();
          return;
        }
        
        // 如果播放器未就绪，使用命令队列（保持原有逻辑）
        const command = async () => {
          if (playerRef.current && typeof playerRef.current.seekTo === 'function') {
            isUserSeekingRef.current = true;
            
            playerRef.current.seekTo(time, allowSeekAhead);
            
            setInternalState(prev => ({
              ...prev,
              currentTime: time
            }));
            
            setTimeout(() => {
              isUserSeekingRef.current = false;
            }, 1500);
          }
          resolve();
        };
        commandQueueRef.current.push(command);
        processCommandQueue();
      });
    },
    
    playVideo: async () => {
      return new Promise((resolve) => {
        const command = async () => {
          if (playerRef.current && typeof playerRef.current.playVideo === 'function') {
            playerRef.current.playVideo();
          }
          resolve();
        };
        commandQueueRef.current.push(command);
        processCommandQueue();
      });
    },
    
    pauseVideo: async () => {
      return new Promise((resolve) => {
        const command = async () => {
          if (playerRef.current && typeof playerRef.current.pauseVideo === 'function') {
            playerRef.current.pauseVideo();
          }
          resolve();
        };
        commandQueueRef.current.push(command);
        processCommandQueue();
      });
    },
    
    getCurrentTime: () => {
      if (playerRef.current && typeof playerRef.current.getCurrentTime === 'function') {
        try {
          return playerRef.current.getCurrentTime();
        } catch (error) {
          // 静默处理错误
        }
      }
      return internalState.currentTime;
    },
    
    getDuration: () => {
      if (playerRef.current && typeof playerRef.current.getDuration === 'function') {
        try {
          return playerRef.current.getDuration();
        } catch (error) {
          // 静默处理错误
        }
      }
      return internalState.duration;
    },
    
    setPlaybackRate: (rate: number) => {
      const command = async () => {
        if (playerRef.current && typeof playerRef.current.setPlaybackRate === 'function') {
          playerRef.current.setPlaybackRate(rate);
        }
      };
      commandQueueRef.current.push(command);
      processCommandQueue();
    },
    
    getPlayerState: () => {
      if (playerRef.current && typeof playerRef.current.getPlayerState === 'function') {
        try {
          return playerRef.current.getPlayerState();
        } catch (error) {
          // 静默处理错误
        }
      }
      return internalState.playerState;
    }
  }), [internalState.currentTime, internalState.duration, internalState.playerState]);

  // 异步命令处理队列 - 确保命令按顺序执行，避免竞态条件
  const processCommandQueue = useCallback(async () => {
    if (processingCommandRef.current || !internalState.isReady || !playerRef.current) {
      return;
    }
    
    processingCommandRef.current = true;
    
    while (commandQueueRef.current.length > 0) {
      const command = commandQueueRef.current.shift();
      if (command) {
        try {
          await command();
          // 给命令执行一些时间，避免过快的连续操作
          await new Promise(resolve => setTimeout(resolve, 50));
        } catch (error) {
          // Command execution error
        }
      }
    }
    
    processingCommandRef.current = false;
  }, [internalState.isReady]);

  // 当播放器就绪时处理队列中的命令
  useEffect(() => {
    if (internalState.isReady) {
      processCommandQueue();
    }
  }, [internalState.isReady, processCommandQueue]);

  // 每次videoId变更时重置错误
  useEffect(() => {
    if (!videoId) {
      setPlayerError((prev) => {
        if (prev?.code === 'missing_id') {
          return prev;
        }
        return { code: 'missing_id', message: 'Video unavailable: missing YouTube ID.' };
      });
    } else {
      setPlayerError(null);
    }
  }, [videoId]);

  // 加载YouTube API
  useEffect(() => {
    if (window.YT && window.YT.Player) {
      setIsAPIReady(true);
      return;
    }

    const script = document.createElement('script');
    script.src = 'https://www.youtube.com/iframe_api';
    script.async = true;

    window.onYouTubeIframeAPIReady = () => {
      setIsAPIReady(true);
    };

    document.head.appendChild(script);

    return () => {
      if (script.parentNode) {
        script.parentNode.removeChild(script);
      }
    };
  }, []);

  // 创建YouTube播放器 - 只在真正需要时创建，避免重复创建
  useEffect(() => {
    if (!isAPIReady || !containerRef.current || !videoId || playerError) return;
    
    // 清理现有播放器
    if (playerRef.current && typeof playerRef.current.destroy === 'function') {
      playerRef.current.destroy();
      playerRef.current = null;
    }

    let youtubePlayer: any = null;

    try {
      youtubePlayer = new window.YT.Player(containerRef.current, {
      videoId: videoId,
      playerVars: {
        autoplay: 0,
        controls: 1,
        disablekb: 0,
        enablejsapi: 1,
        modestbranding: 1,
        rel: 0,
        showinfo: 0,
        origin: window.location.origin,
      },
      events: {
        onReady: (event: any) => {
          playerRef.current = event.target;
          
          // 设置初始状态
          if (muted) {
            event.target.mute();
          }
          
          // 获取时长并更新内部状态
          const duration = event.target.getDuration();
          
          setInternalState(prev => ({
            ...prev,
            isReady: true,
            duration: duration || 0
          }));
          
          if (duration && callbackRefs.current.onDurationChange) {
            callbackRefs.current.onDurationChange(duration);
          }

          // 设置初始时间
          if (currentTime && currentTime > 0) {
            event.target.seekTo(currentTime, true);
          }

          // 通知父组件播放器准备就绪 - 延迟一下确保playerRef.current已设置
          setTimeout(() => {
            if (callbackRefs.current.onReady) {
              callbackRefs.current.onReady(playerControlInterface);
            }
            // 处理可能在播放器就绪前排队的命令
            processCommandQueue();
          }, 100);
        },
        
        onStateChange: (event: any) => {
          const state = event.data;
          const isNowPlaying = state === window.YT.PlayerState.PLAYING;
          const currentTime = playerRef.current ? playerRef.current.getCurrentTime() : 0;
          
          // 更新内部状态
          setInternalState(prev => ({
            ...prev,
            isPlaying: isNowPlaying,
            playerState: state,
            currentTime: currentTime
          }));
          
          // 通知外部状态变化
          if (callbackRefs.current.onPlayPause) {
            callbackRefs.current.onPlayPause(isNowPlaying);
          }
          if (state === window.YT?.PlayerState?.ENDED) {
            callbackRefs.current.onEnded?.();
          }
        },
        onError: (event: any) => {
          const code = event?.data;
          let message = 'Unable to load YouTube video.';

          if (code === 2) {
            message = 'Video unavailable: invalid YouTube ID.';
          } else if (code === 100) {
            message = 'Video unavailable: the video has been removed or is private.';
          } else if (code === 101 || code === 150) {
            message = 'Video unavailable: playback is disabled by the video owner.';
          }

          console.error('YouTube Player error', { code, event });
          setPlayerError({ code: code ?? 'unknown', message });
          setInternalState(prev => ({
            ...prev,
            isReady: false,
            isPlaying: false
          }));

          if (callbackRefs.current.onPlayPause) {
            callbackRefs.current.onPlayPause(false);
          }

          if (playerRef.current && typeof playerRef.current.stopVideo === 'function') {
            try {
              playerRef.current.stopVideo();
            } catch (stopError) {
              // ignore stop errors
            }
          }

          if (playerRef.current && typeof playerRef.current.destroy === 'function') {
            try {
              playerRef.current.destroy();
            } catch (destroyError) {
              // ignore destroy errors
            }
          }

          playerRef.current = null;
          commandQueueRef.current = [];
          processingCommandRef.current = false;
        }
      },
    });
    } catch (error) {
      console.error('Failed to initialize YouTube Player', error);
      setPlayerError({ code: 'init_error', message: 'Unable to load YouTube player with the provided video ID.' });
      return;
    }

    // 设置时间更新循环
    const timeUpdateInterval = setInterval(() => {
      if (playerRef.current && typeof playerRef.current.getCurrentTime === 'function') {
        try {
          const time = playerRef.current.getCurrentTime();
          
          // 🔥 修复：检查是否正在进行用户seek操作，如果是则跳过时间更新
          if (!isUserSeekingRef.current) {
            setInternalState(prev => ({
              ...prev,
              currentTime: time
            }));
            
            if (callbackRefs.current.onTimeUpdate) {
              callbackRefs.current.onTimeUpdate(time);
            }
          }
        } catch (error) {
          // 静默处理错误
        }
      }
    }, 1000);

    return () => {
      clearInterval(timeUpdateInterval);
      if (youtubePlayer && typeof youtubePlayer.destroy === 'function') {
        youtubePlayer.destroy();
      }
    };
  }, [isAPIReady, videoId, muted, playerError]); // 移除函数依赖，防止不必要的重建

  // 处理外部播放状态变化 - 使用命令模式而不是直接状态同步
  useEffect(() => {
    // 添加更严格的检查条件
    if (!internalState.isReady || !playerRef.current || isPlaying === undefined) {
      return;
    }
    
    // 只有当外部状态与内部状态真正不同时才执行命令
    if (isPlaying !== internalState.isPlaying) {
      if (isPlaying) {
        playerControlInterface.playVideo();
      } else {
        playerControlInterface.pauseVideo();
      }
    }
  }, [isPlaying, internalState.isReady, internalState.isPlaying, playerControlInterface]);

  // 添加一个ref来跟踪是否正在进行用户拖拽操作
  const isUserSeekingRef = useRef(false);
  const lastExternalTimeRef = useRef<number>(0);
  
  // 处理外部时间同步 - 修复：只在特定情况下同步，避免播放时回退和干扰用户操作
  useEffect(() => {
    if (!internalState.isReady || currentTime === undefined || currentTime === null) return;

    // 🔥 关键修复：检测是否为用户主动的seek操作
    const timeDiff = Math.abs(internalState.currentTime - currentTime);
    const isNewExternalTime = currentTime !== lastExternalTimeRef.current;
    const hasMeaningfulDiff = timeDiff > 0.5;
    
    // 更新上次外部时间
    lastExternalTimeRef.current = currentTime;
    
    // 如果变化足够明显且是新的外部时间，执行seek
    if (isNewExternalTime && hasMeaningfulDiff) {
      isUserSeekingRef.current = true;
      // 立即执行seek操作，不管播放状态
      playerControlInterface.seekTo(currentTime, true);
      
      // 设置一个短暂的延迟后清除seeking标志
      setTimeout(() => {
        isUserSeekingRef.current = false;
      }, 2000); // 延长到2秒，确保seek操作完全完成
      return;
    }
    
    // 如果正在seeking，不要进行任何同步
    if (isUserSeekingRef.current) {
      return;
    }
    
    // 原有的同步逻辑，但只在初始化时使用
    const isPlayerPaused = internalState.playerState === window.YT?.PlayerState?.PAUSED;
    const isPlayerUnstarted = internalState.playerState === window.YT?.PlayerState?.UNSTARTED;
    const isInitialState = internalState.currentTime < 1;
    
    // 只在初始状态下进行时间同步
    if ((isPlayerUnstarted || isInitialState) && timeDiff > 1) {
      playerControlInterface.seekTo(currentTime, true);
    }
  }, [currentTime, internalState.isReady, internalState.currentTime, internalState.playerState, playerControlInterface]);

  return (
    <div className={`relative aspect-video w-full ${className || ''}`}>
      <div 
        ref={containerRef}
        className="absolute inset-0 w-full h-full"
        style={{ minHeight: '200px' }}
      />
      {playerError && (
        <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 bg-black/80 text-center px-4">
          <div className="text-white text-sm font-medium">{playerError.message}</div>
          <div className="text-white/70 text-xs">(Error code: {playerError.code})</div>
        </div>
      )}
      {!isAPIReady && !playerError && (
        <div className="absolute inset-0 flex items-center justify-center bg-black">
          <div className="text-white">Loading YouTube player...</div>
        </div>
      )}
    </div>
  );
}
